import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { Page } from '../lib/api';
import { dateTime, humanise, mobile as fmtMobile, num } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Badge, Button, Chips, Empty, Modal, Notice, useToast } from '../components/ui';
import type { BadgeTone } from '../components/ui';

interface ThreadRow {
  id: string;
  kind: string;
  title: string | null;
  lastMessageAt: string | null;
  createdAt: string;
  listing: { id: string; title: string } | null;
  participants: Array<{ user: { id: string; mobile: string; displayName: string | null } }>;
  _count: { messages: number };
}

interface MessageRow {
  id: string;
  body: string;
  isBot: boolean;
  mediaKey: string | null;
  createdAt: string;
  sender: { id: string; mobile: string; displayName: string | null } | null;
}

/**
 * v2 §30 chat moderation.
 *
 * Conversations are readable here because moderating a report about one
 * requires reading it. That is a real privacy trade-off, so it is confined to
 * admin, and deleting a message records the deleted body in the audit trail —
 * a deletion that leaves no record of what was removed is useless in a dispute.
 */
export function ChatModerationPage() {
  const { get, set } = useListParams();
  const [openThread, setOpenThread] = useState<ThreadRow | null>(null);
  const kind = get('kind');
  const toast = useToast();
  const qc = useQueryClient();

  const messages = useQuery({
    queryKey: ['thread-messages', openThread?.id],
    queryFn: () => api.get<Page<MessageRow>>(`/admin/chat/threads/${openThread?.id}/messages?limit=100`),
    enabled: Boolean(openThread),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/admin/chat/messages/${id}`),
    onSuccess: () => {
      toast('Message deleted — the body is kept in the audit trail', 'success');
      qc.invalidateQueries({ queryKey: ['thread-messages', openThread?.id] });
      qc.invalidateQueries({ queryKey: ['threads'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const columns: Array<Column<ThreadRow>> = [
    {
      key: 'thread',
      header: 'Conversation',
      render: (r) => (
        <div>
          <div className="cell-strong">{r.title ?? r.listing?.title ?? humanise(r.kind)}</div>
          <div className="cell-muted">
            {r.participants.map((p) => p.user.displayName ?? fmtMobile(p.user.mobile)).join(' ↔ ') || 'no participants'}
          </div>
        </div>
      ),
    },
    { key: 'kind', header: 'Type', render: (r) => <Badge tone={r.kind === 'CRM_BOT' ? 'purple' : 'blue'}>{humanise(r.kind)}</Badge> },
    { key: 'count', header: 'Messages', numeric: true, render: (r) => num(r._count.messages) },
    { key: 'last', header: 'Last message', render: (r) => <span className="cell-muted">{dateTime(r.lastMessageAt)}</span> },
    { key: 'created', header: 'Started', render: (r) => <span className="cell-muted">{dateTime(r.createdAt)}</span> },
  ];

  const view = get('view') || 'threads';

  return (
    <div className="stack">
      <Chips
        value={view}
        onChange={(v) => set('view', v)}
        options={[
          { value: 'threads', label: 'Conversations' },
          { value: 'contact', label: 'Contact requests' },
          { value: 'blocks', label: 'Blocked users' },
          { value: 'audit', label: 'Audit trail' },
        ]}
      />
      {view === 'contact' && <ContactRequestsTable />}
      {view === 'blocks' && <BlocksTable />}
      {view === 'audit' && <ContactAuditTable />}
      {view === 'threads' && (
      <>
      <Notice tone="warn" title="Private conversations">
        A conversation opens only to investigate an open report about one of its people or messages (super admins
        excepted), and every opening is recorded against you. Phone numbers stay masked. Chat abuse reports are on the
        Reports page.
      </Notice>

      <DataTable<ThreadRow>
        queryKey={['threads', kind]}
        path="/admin/chat/threads"
        params={{ kind }}
        columns={columns}
        onRowClick={setOpenThread}
        emptyIcon="✉"
        emptyTitle="No conversations"
        toolbar={
          <Chips
            value={kind}
            onChange={(v) => set('kind', v)}
            options={[
              { value: '', label: 'All' },
              { value: 'LISTING', label: 'About a listing' },
              { value: 'DIRECT', label: 'Direct' },
              { value: 'GROUP', label: 'Group' },
              { value: 'CRM_BOT', label: 'CRM bot' },
            ]}
          />
        }
      />

      {openThread && (
        <Modal
          wide
          title={openThread.title ?? openThread.listing?.title ?? humanise(openThread.kind)}
          sub={openThread.participants.map((p) => p.user.displayName ?? fmtMobile(p.user.mobile)).join(' ↔ ')}
          onClose={() => setOpenThread(null)}
          footer={<Button onClick={() => setOpenThread(null)}>Close</Button>}
        >
          {messages.isLoading ? (
            <div className="skeleton" style={{ height: 220 }} />
          ) : messages.error ? (
            <Notice tone="warn" title="Not without a report">
              {(messages.error as Error).message}
            </Notice>
          ) : !messages.data?.items.length ? (
            <Empty icon="✉" title="No messages in this conversation" />
          ) : (
            <div className="stack" style={{ gap: 10 }}>
              {messages.data.items.map((m) => (
                <div
                  key={m.id}
                  className="row"
                  style={{
                    alignItems: 'flex-start',
                    background: m.isBot ? 'var(--tint-purple)' : 'var(--bg)',
                    padding: '10px 13px',
                    borderRadius: 'var(--r-chip)',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-muted)' }}>
                      {m.isBot ? 'AI assistant' : (m.sender?.displayName ?? fmtMobile(m.sender?.mobile))}
                      <span style={{ fontWeight: 500, marginLeft: 8 }}>{dateTime(m.createdAt)}</span>
                    </div>
                    <div style={{ fontSize: 13.5, lineHeight: 1.55, marginTop: 3, whiteSpace: 'pre-wrap' }}>{m.body}</div>
                    {m.mediaKey && <div className="cell-mono">attachment: {m.mediaKey}</div>}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    title="Delete this message"
                    loading={remove.isPending && remove.variables === m.id}
                    onClick={() => remove.mutate(m.id)}
                  >
                    Delete
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}
      </>
      )}
    </div>
  );
}

interface ContactRequestRow {
  id: string;
  channel: 'CALL' | 'WHATSAPP';
  status: 'REQUESTED' | 'APPROVED' | 'REJECTED' | 'REVOKED';
  requestedAt: string;
  decidedAt: string | null;
  revokedAt: string | null;
  thread: { listing: { id: string; title: string } | null };
  requester: { id: string; displayName: string | null };
  granter: { id: string; displayName: string | null };
}

const REQUEST_TONE: Record<ContactRequestRow['status'], BadgeTone> = {
  REQUESTED: 'amber',
  APPROVED: 'green',
  REJECTED: 'red',
  REVOKED: 'neutral',
};

/** Who asked whom for Call or WhatsApp access, and where it stands. Never the numbers. */
function ContactRequestsTable() {
  const { get, set } = useListParams();
  const status = get('status');
  const columns: Array<Column<ContactRequestRow>> = [
    {
      key: 'who',
      header: 'Asked by → of',
      render: (r) => (
        <div>
          <div className="cell-strong">
            {r.requester.displayName ?? 'Unnamed'} → {r.granter.displayName ?? 'Unnamed'}
          </div>
          <div className="cell-muted">{r.thread.listing?.title ?? 'Direct chat'}</div>
        </div>
      ),
    },
    { key: 'channel', header: 'For', render: (r) => (r.channel === 'CALL' ? 'Phone call' : 'WhatsApp') },
    { key: 'status', header: 'Status', render: (r) => <Badge tone={REQUEST_TONE[r.status]}>{humanise(r.status)}</Badge> },
    { key: 'asked', header: 'Asked', render: (r) => <span className="cell-muted">{dateTime(r.requestedAt)}</span> },
    {
      key: 'decided',
      header: 'Answered / revoked',
      render: (r) => <span className="cell-muted">{dateTime(r.revokedAt ?? r.decidedAt)}</span>,
    },
  ];
  return (
    <DataTable<ContactRequestRow>
      queryKey={['contact-permissions', status]}
      path="/admin/chat/contact-permissions"
      params={{ status }}
      columns={columns}
      emptyIcon="☎"
      emptyTitle="No contact requests"
      toolbar={
        <Chips
          value={status}
          onChange={(v) => set('status', v)}
          options={[
            { value: '', label: 'All' },
            { value: 'REQUESTED', label: 'Waiting' },
            { value: 'APPROVED', label: 'Approved' },
            { value: 'REJECTED', label: 'Rejected' },
            { value: 'REVOKED', label: 'Revoked' },
          ]}
        />
      }
    />
  );
}

interface BlockRow {
  id: string;
  createdAt: string;
  blocker: { id: string; displayName: string | null; mobile: string };
  blocked: { id: string; displayName: string | null; mobile: string };
}

function BlocksTable() {
  const columns: Array<Column<BlockRow>> = [
    {
      key: 'blocker',
      header: 'Blocked by',
      render: (r) => (
        <Link to={`/users/${r.blocker.id}`} onClick={(e) => e.stopPropagation()}>
          {r.blocker.displayName ?? r.blocker.mobile}
        </Link>
      ),
    },
    {
      key: 'blocked',
      header: 'Blocked',
      render: (r) => (
        <Link to={`/users/${r.blocked.id}`} onClick={(e) => e.stopPropagation()}>
          {r.blocked.displayName ?? r.blocked.mobile}
        </Link>
      ),
    },
    { key: 'when', header: 'Since', render: (r) => <span className="cell-muted">{dateTime(r.createdAt)}</span> },
  ];
  return (
    <DataTable<BlockRow>
      queryKey={['chat-blocks']}
      path="/admin/chat/blocks"
      params={{}}
      columns={columns}
      emptyIcon="⛔"
      emptyTitle="Nobody has blocked anybody"
    />
  );
}

interface AuditRow {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  createdAt: string;
  actor: { id: string; displayName: string | null } | null;
  after: Record<string, unknown> | null;
}

/** Requests, approvals, rejections, revocations, blocks and conversation readings. */
function ContactAuditTable() {
  const { get, set } = useListParams();
  const action = get('action') || 'CONTACT';
  const columns: Array<Column<AuditRow>> = [
    { key: 'when', header: 'When', render: (r) => <span className="cell-muted">{dateTime(r.createdAt)}</span> },
    { key: 'action', header: 'What', render: (r) => <span className="cell-strong">{humanise(r.action)}</span> },
    { key: 'actor', header: 'Who', render: (r) => r.actor?.displayName ?? r.actor?.id ?? '—' },
    {
      key: 'detail',
      header: 'Detail',
      render: (r) => (
        <span className="cell-muted">
          {r.after && typeof r.after === 'object' && 'channel' in r.after ? `${String(r.after.channel)} · ${String(r.after.status ?? '')}` : r.entityType}
        </span>
      ),
    },
  ];
  return (
    <DataTable<AuditRow>
      queryKey={['chat-audit', action]}
      path="/admin/audit"
      params={{ action }}
      columns={columns}
      emptyIcon="🧾"
      emptyTitle="Nothing recorded yet"
      toolbar={
        <Chips
          value={action}
          onChange={(v) => set('action', v)}
          options={[
            { value: 'CONTACT', label: 'Contact access' },
            { value: 'BLOCK', label: 'Blocks' },
            { value: 'CHAT_VIEW', label: 'Conversations opened' },
          ]}
        />
      }
    />
  );
}
